import { Avatar, Box, CircularProgress, Container, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "lib/api/api";
import Meta from "../Meta/Meta";

const teamMembers = [
  {
    name: "Mafdy Farouk",
    roleKey: "team.roles.ceoTeacher",
    image: "/team-mafdy-farouk.jpg",
  },
  {
    name: "Mohamed Abdulsattar",
    roleKey: "team.roles.teacherMale",
    image: "/team-mohamed-abdulsattar.jpg",
  },
  {
    name: "Miriam Dirnberger",
    roleKey: "team.roles.teacherFemale",
    image: "/team-miriam-dirnberger.jpg",
  },
  {
    name: "Sara Elrefaey",
    roleKey: "team.roles.teacherFemale",
    image: "/team-sara-elrefaey.jpg",
  },
  {
    name: "Erik Lutz",
    roleKey: "team.roles.interculturalTrainer",
    image: "/team-erik-lutz.jpeg",
  },
  {
    name: "Hoda Mostafa",
    roleKey: "team.roles.teacherFemale",
    image: "/team-hoda-mostafa.jpg",
  },
  {
    name: "Fatema Alzahraa",
    roleKey: "team.roles.teacherFemale",
    image: "/team-fatema-alzahraa.JPG",
  },
  {
    name: "Yara Ahmed",
    roleKey: "team.roles.teacherFemale",
    image: "/team-yara-ahmed.JPG",
  },
];

const getTeacherImageSrc = (value, fallback) => {
  const src = String(value || fallback || "").trim();
  if (!src) return "";
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_API_URL}${src}`;
  if (/^(https?:)?\/\//i.test(src) || src.startsWith("/")) return src;
  return `/${src}`;
};

const findFallbackMember = (teacher) => {
  const fullName = `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim().toLowerCase();
  const normalizedName = fullName.replace(/[^a-z0-9]/g, "");
  return teamMembers.find(
    (member) => member.name.toLowerCase().replace(/[^a-z0-9]/g, "") === normalizedName
  );
};

export default function TeamPage() {
  const { t, i18n } = useTranslation(["ui"]);
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const language = i18n.language?.split("-")[0];
  const localized = (defaultValue, arValue, deValue) => {
    if (language === "ar" && arValue) return arValue;
    if (language === "de" && deValue) return deValue;
    return defaultValue;
  };

  useEffect(() => {
    let ignore = false;

    const loadTeachers = async () => {
      try {
        const response = await api.get("/courses/teachers");
        if (!ignore) setTeachers(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        if (!ignore) setTeachers([]);
      } finally {
        if (!ignore) setLoadingTeachers(false);
      }
    };

    loadTeachers();
    return () => {
      ignore = true;
    };
  }, []);

  const displayMembers =
    teachers.length > 0
      ? teachers
          .slice()
          .sort(
            (a, b) =>
              (a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
                (b.displayOrder ?? Number.MAX_SAFE_INTEGER) ||
              `${a.firstName || ""} ${a.lastName || ""}`.localeCompare(
                `${b.firstName || ""} ${b.lastName || ""}`
              )
          )
          .map((teacher) => {
          const fallback = findFallbackMember(teacher);
          return {
            id: teacher.teacherId,
            businessKey: teacher.businessKey,
            name: `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim(),
            role:
              teacher.job ||
              localized(teacher.specialization, teacher.specializationAr, teacher.specializationDe) ||
              t(fallback?.roleKey || "team.roles.teacherMale", { defaultValue: "Teacher" }),
            image: getTeacherImageSrc(teacher.profilePicture, fallback?.image),
            fallbackImage: fallback?.image,
          };
        })
      : teamMembers.map((member) => ({
          name: member.name,
          role: t(member.roleKey),
          image: member.image,
          fallbackImage: member.image,
        }));

  return (
    <>
      <Meta
        title="ALGermanist - Team"
        description="Meet the ALGermanist team of teachers and advisors."
        keywords="ALGermanist team, German teachers, ALGermanist team"
      />
      <Box sx={{ bgcolor: "#F4F4F2", pb: { xs: 6, md: 10 } }}>
        <Box
          sx={{
            position: "relative",
            mb: { xs: 4, md: 7 },
            height: { xs: 260, md: 520 },
            backgroundImage: "url('/home-heidelberg-bg.png')",
            backgroundSize: "cover",
            backgroundPosition: {
              xs: "38% 9%",
              sm: "42% 9%",
              md: "center 9%",
            },
            bgcolor: "#EAEFF2",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 42%, rgba(0,0,0,0) 72%)",
            }}
          />
          <Container
            maxWidth="xl"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              px: { xs: 3, md: 8 },
            }}
          >
            <Typography
              variant="h1"
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontSize: { xs: "42px", sm: "58px", md: "76px" },
                lineHeight: 1.05,
                textShadow: "0 3px 12px rgba(0,0,0,0.45)",
              }}
            >
              {t("team.title", { defaultValue: "Our Team" })}
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 6 } }}>
          {loadingTeachers && (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <CircularProgress />
            </Box>
          )}
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {displayMembers.map((member) => (
              <Grid item xs={12} sm={6} md={3} key={member.name}>
                <Box
                  onClick={() => (member.businessKey || member.id) && navigate(`/teachers/${member.businessKey || member.id}`)}
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: "16px",
                    p: 2,
                    height: "100%",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.22)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: member.businessKey || member.id ? "pointer" : "default",
                  }}
                >
                  {member.image ? (
                    <Box
                      component="img"
                      src={member.image}
                      alt={member.name}
                      onError={(event) => {
                        if (member.fallbackImage && event.currentTarget.src !== window.location.origin + member.fallbackImage) {
                          event.currentTarget.src = member.fallbackImage;
                        } else {
                          event.currentTarget.style.display = "none";
                        }
                      }}
                      sx={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        objectFit: "cover",
                        borderRadius: "12px",
                        bgcolor: "#E9ECEF",
                        mb: 1.5,
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        height: "auto",
                        borderRadius: "12px",
                        bgcolor: "#FFD600",
                        color: "#111",
                        mb: 1.5,
                        fontSize: "3rem",
                        fontWeight: 800,
                      }}
                    >
                      {member.name
                        .split(" ")
                        .map((part) => part[0])
                        .join("")
                        .slice(0, 2)}
                    </Avatar>
                  )}
                  <Typography
                    fontWeight={800}
                    sx={{
                      fontSize: { xs: "1rem", md: "1.05rem" },
                      lineHeight: 1.15,
                      textAlign: "center",
                    }}
                  >
                    {member.name}
                  </Typography>
                  <Typography
                    fontWeight={700}
                    sx={{
                      fontSize: "0.78rem",
                      lineHeight: 1.2,
                      textAlign: "center",
                      color: "#111",
                    }}
                  >
                    {member.role}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
}
